/** PicklistLayoutSlot — renders picklist categories whose
 *  lbs_picklist_field_layout row targets the current tab.
 *
 *  Tenant override > global default precedence preserved verbatim
 *  from the legacy DynamicPicklistFields.
 *
 *  Engine passes the current tab via `ctx.currentTab` and the
 *  configured exclude list (e.g. cold_chain on Compliance) via
 *  `componentProps.exclude`. */
import { useEffect, useState, useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { listPicklistFieldLayout } from "@/lib/api";

const FIELD_META = {
  vehicle_type:     { label: "Vehicle type", required: true },
  goods_category:   { label: "Goods category" },
  pallet_type:      { label: "Pallet brand" },
  load_rule:        { label: "Load rule" },
  restraint_method: { label: "Restraint method" },
  cold_chain:       { label: "Cold chain" },
};

export default function PicklistLayoutSlot({ values, set, picks, ctx, exclude = [] }) {
  const { currentTab, clientId } = ctx;
  const [layout, setLayout] = useState([]);

  useEffect(() => {
    if (!clientId) { setLayout([]); return undefined; }
    let cancelled = false;
    (async () => {
      const { data } = await listPicklistFieldLayout(clientId);
      if (!cancelled) setLayout(data || []);
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const fieldsForThisTab = useMemo(() => {
    const out = [];
    Object.keys(FIELD_META).forEach((category) => {
      if (exclude.includes(category)) return;
      const tenantRow = layout.find((r) => r.category === category && r.client_id === clientId);
      const globalRow = layout.find((r) => r.category === category && r.client_id === null);
      const effective = tenantRow || globalRow;
      if (!effective || effective.target_tab !== currentTab) return;
      out.push({ category, ...effective });
    });
    out.sort((a, b) => (a.target_position ?? 0) - (b.target_position ?? 0));
    return out;
  }, [layout, currentTab, clientId, exclude]);

  if (!fieldsForThisTab.length) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {fieldsForThisTab.map((f) => {
        const meta = FIELD_META[f.category];
        const options = (picks && picks[f.category]) || [];
        return (
          <div key={f.category} className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              {meta.label}
              {meta.required && <span className="text-rose-600 ml-0.5" title="Required field" data-testid={`required-mark-${f.category}`}>*</span>}
            </Label>
            <Select value={values[f.category] || ""} onValueChange={(v) => set(f.category, v)}>
              <SelectTrigger data-testid={`booking-${f.category.replace(/_/g, "-")}-select`}>
                <SelectValue placeholder="(none)" />
              </SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label || o.value}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      })}
    </div>
  );
}
